/**
 * Telephony event bus + Supabase logger.
 * All emits fan out to local subscribers and are batched into the
 * `telephony_events` table. Non-blocking: failures re-queue up to a cap.
 */

import { supabase } from '../supabase';
import type { TelephonyEvent } from './types';

type Listener = (e: TelephonyEvent) => void;

const listeners = new Set<Listener>();
const queue: TelephonyEvent[] = [];
const MAX_QUEUE = 500;
const FLUSH_DELAY_MS = 500;
const BATCH_SIZE = 50;

let flushTimer: ReturnType<typeof setTimeout> | null = null;
let userId: string | null = null;

export function setEventUser(id: string | null): void {
  userId = id;
}

export function emit(event: Omit<TelephonyEvent, 'timestamp'>): void {
  const full: TelephonyEvent = { ...event, timestamp: new Date() };

  listeners.forEach((l) => {
    try {
      l(full);
    } catch (err) {
      console.error('[telephony] listener error', err);
    }
  });

  queue.push(full);
  scheduleFlush();

  if (import.meta.env.DEV) {
    console.debug('[telephony]', full.type, full.metadata ?? '');
  }
}

export function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function scheduleFlush(): void {
  if (flushTimer) return;
  flushTimer = setTimeout(flush, FLUSH_DELAY_MS);
}

async function flush(): Promise<void> {
  flushTimer = null;
  if (!queue.length) return;

  const batch = queue.splice(0, BATCH_SIZE);

  try {
    const { error } = await supabase.from('telephony_events').insert(
      batch.map((e) => ({
        client_call_id: e.clientCallId ?? null,
        call_id: e.callId ?? null,
        user_id: userId,
        event_type: e.type,
        source: e.source,
        metadata: e.metadata ?? {},
        created_at: e.timestamp.toISOString(),
      })),
    );
    if (error) throw error;
  } catch (err) {
    console.warn('[telephony] event flush failed, retrying', err);
    const room = Math.max(0, MAX_QUEUE - queue.length);
    queue.unshift(...batch.slice(0, room));
    scheduleFlush();
  }
}
